import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Podcast } from '@/types/Podcast';

export const usePodcasts = () => {
  const [allPodcasts, setAllPodcasts] = useState<Podcast[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Load only essential data first (pagination)
  useEffect(() => {
    const loadInitialPodcasts = async () => {
      try {
        // Load only first 20 podcasts for initial render with minimal data
        const { data, error } = await supabase
          .from('podcasts')
          .select('*')
          .order('Name')
          .limit(20);
          
        if (error) throw error;
        
        setAllPodcasts(data || []);
        
        // Load remaining data asynchronously
        setTimeout(async () => {
          const { data: allData, error: allError } = await supabase
            .from('podcasts')
            .select('*')
            .order('Name');
            
          if (allError) throw allError;
          setAllPodcasts(allData || []);
        }, 100);
        
      } catch (error) {
        console.error('Error loading podcasts:', error);
        toast({
          title: 'Problème de connexion',
          description: 'Impossible de charger les lieux. Vérifiez votre connexion Internet et actualisez la page.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    loadInitialPodcasts();
  }, [toast]);

  // Normalize text for better search (removes accents, diacritics)
  const normalizeText = (text: string) => {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  };

  // Memoized search function with accent-insensitive search
  const searchPodcasts = useMemo(() => {
    return (query: string) => {
      if (!query.trim()) return allPodcasts;
      
      // Nettoyer la chaîne de recherche en supprimant les accents, les tirets et en la passant en minuscules
      const normalizedQuery = normalizeText(query.replace(/[-\s]/g, ''));

      return allPodcasts.filter(podcast => {
        // Combiner et normaliser les champs de recherche pour une correspondance plus large
        const normalizedName = normalizeText(podcast.Name.replace(/[-\s]/g, ''));
        const normalizedCity = normalizeText(podcast.City.replace(/[-\s]/g, ''));
        const normalizedCountry = normalizeText(podcast.Country.replace(/[-\s]/g, ''));

        // Vérifier si la requête normalisée est incluse dans l'un des champs normalisés
        return (
          normalizedName.includes(normalizedQuery) ||
          normalizedCity.includes(normalizedQuery) ||
          normalizedCountry.includes(normalizedQuery)
        );
      });
    };
  }, [allPodcasts]);

  return {
    podcasts: allPodcasts,
    loading,
    searchPodcasts
  };
};